import React, { useState } from "react";
import { X, Calendar } from "lucide-react";
import HolidayForm from "./HolidayForm";
import HolidayFilters from "./HolidayFilters";
import HolidayTable from "./HolidayTable";
import DepartmentPickerModal from "./Departmentpickermodal";

/**
 * HolidayModal — works in two modes:
 *
 * MODE A (hook spread — recommended):
 *   const holidays = useHolidays({ profile, language, t, onAlert });
 *   <HolidayModal show={show} onClose={onClose} theme={theme} isRTL={isRTL} language={language} t={t} {...holidays} />
 *
 * MODE B (legacy explicit props):
 *   <HolidayModal show={show} onClose={onClose} ... onFormChange={fn} onSubmitHoliday={fn} ... />
 *
 * The dept-picker state is managed INTERNALLY when no external open/close
 * handlers are provided, so no parent changes are needed.
 */
const HolidayModal = ({
  show, onClose,
  theme, isRTL, language, t,

  /* ── form ── */
  holidayForm,
  handleHolidayFormChange, onFormChange,
  editingHoliday,
  handleCancelEditHoliday, onCancelEdit,
  holidayCompanies,
  handleSubmitHoliday,     onSubmitHoliday,
  holidaySubmitting,

  /* ── filters ── */
  holidaySearch,       setHolidaySearch,       onSearchChange,
  holidayClientFilter, setHolidayClientFilter, onClientFilterChange,
  holidayCompanyFilter,setHolidayCompanyFilter,onCompanyFilterChange,
  holidayClientsList,

  /* ── table ── */
  holidayList, holidayLoading,
  holidayDeleteConfirmId, setHolidayDeleteConfirmId,
  handleStartEditHoliday, onStartEdit,
  handleDeleteHoliday,

  /* ── departments ── (all optional — modal self-manages if absent) */
  holidayDepartments,
  availableDepartments,
  deptPickerOpen:     deptPickerOpenProp,
  showDeptPicker,
  handleOpenDeptPicker,   onOpenDeptPicker,
  handleCloseDeptPicker,  onCloseDeptPicker,
  handleDeptPickerConfirm, onDeptPickerConfirm,
}) => {
  /* ── Internal dept-picker state (used when parent doesn't control it) ── */
  const [internalDeptOpen, setInternalDeptOpen] = useState(false);

  /* Resolve prop aliases — prefer hook names, fall back to legacy names */
  const _onFormChange        = handleHolidayFormChange  ?? onFormChange;
  const _onCancelEdit        = handleCancelEditHoliday  ?? onCancelEdit;
  const _onSubmit            = handleSubmitHoliday      ?? onSubmitHoliday;
  const _onSearchChange      = setHolidaySearch         ?? onSearchChange;
  const _onClientFilter      = setHolidayClientFilter   ?? onClientFilterChange;
  const _onCompanyFilter     = setHolidayCompanyFilter  ?? onCompanyFilterChange;
  const _onStartEdit         = handleStartEditHoliday   ?? onStartEdit;
  const _onDeleteCancel      = setHolidayDeleteConfirmId ? () => setHolidayDeleteConfirmId(null) : undefined;
  const _onDeleteConfirm     = setHolidayDeleteConfirmId;
  const _departments         = holidayDepartments ?? availableDepartments ?? [];

  /*
   * Dept picker open/close:
   * If the parent supplies handlers — use them (external state).
   * Otherwise fall back to internal state so the button always works.
   */
  const hasExternalOpen  = typeof (handleOpenDeptPicker  ?? onOpenDeptPicker)  === "function";
  const hasExternalClose = typeof (handleCloseDeptPicker ?? onCloseDeptPicker) === "function";

  const _deptPickerOpen = hasExternalOpen
    ? !!(deptPickerOpenProp ?? showDeptPicker ?? false)
    : internalDeptOpen;

  const _onOpenDeptPicker = hasExternalOpen
    ? (handleOpenDeptPicker ?? onOpenDeptPicker)
    : () => setInternalDeptOpen(true);

  const _onCloseDeptPicker = hasExternalClose
    ? (handleCloseDeptPicker ?? onCloseDeptPicker)
    : () => setInternalDeptOpen(false);

  const _onDeptPickerConfirm = (() => {
    const external = handleDeptPickerConfirm ?? onDeptPickerConfirm;
    if (typeof external === "function") return external;
    /* Fallback: write directly into form via onFormChange */
    return ({ allDepartments, selectedDepartments }) => {
      if (typeof _onFormChange === "function") {
        _onFormChange("allDepartments", allDepartments);
        _onFormChange("selectedDepartments", selectedDepartments);
      }
      setInternalDeptOpen(false);
    };
  })();

  if (!show) return null;

  const overlayStyle = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(6px)",
    zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px",
  };

  const panelStyle = {
    background: theme === "dark" ? "#1e2533" : "#ffffff",
    borderRadius: "24px",
    padding: "32px",
    width: "100%",
    maxWidth: "860px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
    direction: isRTL ? "rtl" : "ltr",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose}>
        <div style={panelStyle} onClick={(e) => e.stopPropagation()}>

          {/* ── Header ── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ background: "linear-gradient(135deg,#059669,#047857)", borderRadius: "12px", padding: "10px", display: "flex" }}>
                <Calendar size={22} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: "20px", fontWeight: "800", color: theme === "dark" ? "#fff" : "#111827" }}>
                  {t.holidayManagement}
                </div>
                <div style={{ fontSize: "13px", color: theme === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)", marginTop: "2px" }}>
                  {t.holidayManagementDesc}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", cursor: "pointer", color: theme === "dark" ? "#fff" : "#374151", padding: "8px", borderRadius: "8px" }}
            >
              <X size={22} />
            </button>
          </div>

          {/* ── Form ── */}
          <HolidayForm
            theme={theme}
            isRTL={isRTL}
            t={t}
            language={language}
            holidayForm={holidayForm}
            onFormChange={_onFormChange}
            editingHoliday={editingHoliday}
            onCancelEdit={_onCancelEdit}
            holidayCompanies={holidayCompanies}
            onSubmit={_onSubmit}
            submitting={holidaySubmitting}
            onOpenDeptPicker={_onOpenDeptPicker}
          />

          {/* ── Filters ── */}
          <HolidayFilters
            theme={theme}
            isRTL={isRTL}
            language={language}
            t={t}
            holidaySearch={holidaySearch}
            onSearchChange={_onSearchChange}
            holidayClientFilter={holidayClientFilter}
            onClientFilterChange={_onClientFilter}
            holidayCompanyFilter={holidayCompanyFilter}
            onCompanyFilterChange={_onCompanyFilter}
            holidayClientsList={holidayClientsList}
            holidayCompanies={holidayCompanies}
            holidayList={holidayList}
          />

          {/* ── Table ── */}
          <HolidayTable
            theme={theme}
            isRTL={isRTL}
            language={language}
            t={t}
            holidayList={holidayList}
            holidayLoading={holidayLoading}
            holidayDeleteConfirmId={holidayDeleteConfirmId}
            onStartEdit={_onStartEdit}
            onDeleteConfirm={_onDeleteConfirm}
            onDeleteCancel={_onDeleteCancel}
            onDeleteExecute={handleDeleteHoliday}
          />
        </div>
      </div>

      {/* ── Dept picker — above main modal z-index ── */}
      <DepartmentPickerModal
        show={_deptPickerOpen}
        onClose={_onCloseDeptPicker}
        theme={theme}
        isRTL={isRTL}
        departments={_departments}
        selectedDepts={holidayForm?.selectedDepartments || []}
        allSelected={!!holidayForm?.allDepartments}
        onConfirm={_onDeptPickerConfirm}
      />
    </>
  );
};

export default HolidayModal;